import groupBy from 'lodash/groupBy';

import { pluralizeToString } from '~/utils/stringUtils';

import { PRICING_OPTION_LABELS, previewName } from '../constants';
import { BLACKOUT_NAME_TOO_LONG, validateBlackout } from '../tabs/blackouts/blackoutsValidation';
import type { BlackoutDraft } from '../tabs/blackouts/types';
import { findInclusionProblems } from '../tabs/inclusions/inclusionsModel';
import type { InclusionDraftPayload } from '../tabs/inclusions/types';
import { invalidOverrides } from '../tabs/overrides/overridesValidation';
import type { OverridesValues } from '../tabs/overrides/types';
import { DEFAULT_OPTION_STATE, roomsMissingAllocation } from '../tabs/packageOptions/packageOptionsModel';
import type { OptionState } from '../tabs/packageOptions/types';
import { SURCHARGE_NAME_TOO_LONG, validateSurchargePeriod } from '../tabs/surcharges/surchargePeriodValidation';
import type { SurchargePeriod } from '../tabs/surcharges/types';
import type { PricingOptionIndex, PricingToolTab } from '../types';

const NAMES_SHOWN = 3;

const quoteName = (name: string) => (name ? `"${previewName(name)}"` : 'unnamed');

function summarizeNames(names: Array<string>): string {
  const shown = names.slice(0, NAMES_SHOWN).map(quoteName).join(', ');
  return names.length > NAMES_SHOWN ? `${shown} and ${names.length - NAMES_SHOWN} more` : shown;
}

export interface RequiredItemsInput {
  preferredOption: PricingOptionIndex;
  inclusionDrafts: Array<InclusionDraftPayload>;
  optionStates: Record<number, OptionState>;
  overrides: OverridesValues;
  blackouts: Array<BlackoutDraft>;
  surchargePeriods: ReadonlyArray<SurchargePeriod>;
}

interface RequiredItemsSection {
  tab: PricingToolTab;
  summary: string | undefined;
  items: Array<string>;
}

const countLine = (noun: string, count: number, action: string) =>
  count > 0 ? `${pluralizeToString(noun, count)} ${count === 1 ? 'needs' : 'need'} ${action}` : undefined;

// Long names are left out at submit instead of blocking it, so their length errors are dropped here.
function findRequiredItemsSections({
  preferredOption,
  inclusionDrafts,
  optionStates,
  overrides,
  blackouts,
  surchargePeriods,
}: RequiredItemsInput): Array<RequiredItemsSection> {
  const optionLabel = PRICING_OPTION_LABELS[preferredOption];
  const roomsWithoutAllocation = roomsMissingAllocation(optionStates[preferredOption] ?? DEFAULT_OPTION_STATE);
  const inclusionProblems = findInclusionProblems(inclusionDrafts, optionStates, preferredOption);
  const overrideErrors = invalidOverrides(overrides);
  const blackoutErrors = blackouts.map((blackout) =>
    validateBlackout(blackout)
      .filter((error) => error !== BLACKOUT_NAME_TOO_LONG)
      .map((error) => `Blackout ${summarizeNames([blackout.name])}: ${error}`),
  );
  const surchargeErrors = surchargePeriods
    // Unset options can't say whether the period applies, so it is listed rather than skipped.
    .filter((period) => period.options == null || period.options.includes(preferredOption))
    .map((period) =>
      validateSurchargePeriod(period)
        .filter((error) => error !== SURCHARGE_NAME_TOO_LONG)
        .map((error) => `Surcharge period ${summarizeNames([period.name])}: ${error}`),
    );
  const roomsLine = countLine('room', roomsWithoutAllocation.length, 'an allocation');
  const recordsWithErrors = (errorsPerRecord: Array<Array<string>>) =>
    errorsPerRecord.filter((errors) => errors.length > 0).length;

  return [
    {
      tab: 'package-options',
      summary: roomsLine && `${optionLabel}: ${roomsLine}`,
      items:
        roomsWithoutAllocation.length > 0
          ? [
              `${optionLabel} – missing allocation (${pluralizeToString('room', roomsWithoutAllocation.length)}): ${summarizeNames(roomsWithoutAllocation)}`,
            ]
          : [],
    },
    {
      tab: 'inclusions',
      summary: countLine('inclusion', inclusionProblems.length, 'finishing'),
      items: Object.values(
        groupBy(inclusionProblems, ({ pricingOptionIndex, missing }) => [pricingOptionIndex, ...missing].join('|')),
      ).map((problems) => {
        const [{ pricingOptionIndex, missing }] = problems;
        const problem = missing.length > 0 ? `missing ${missing.join(', ')}` : 'applies to no package';
        return `${PRICING_OPTION_LABELS[pricingOptionIndex]} – ${problem} (${pluralizeToString('inclusion', problems.length)}): ${summarizeNames(problems.map(({ name }) => name))}`;
      }),
    },
    {
      tab: 'overrides',
      summary:
        overrideErrors.length > 0
          ? `${pluralizeToString('override problem', overrideErrors.length)} to fix`
          : undefined,
      items: overrideErrors,
    },
    {
      tab: 'blackouts',
      summary: countLine('blackout', recordsWithErrors(blackoutErrors), 'fixing'),
      items: blackoutErrors.flat(),
    },
    {
      tab: 'surcharges',
      summary: countLine('surcharge period', recordsWithErrors(surchargeErrors), 'finishing'),
      items: surchargeErrors.flat(),
    },
  ];
}

export function findMissingRequiredItems(input: RequiredItemsInput): Array<string> {
  return findRequiredItemsSections(input).flatMap(({ items }) => items);
}

export function summarizeMissingRequiredItems(
  input: RequiredItemsInput,
): Array<{ tab: PricingToolTab; summary: string }> {
  return findRequiredItemsSections(input).flatMap(({ tab, summary }) => (summary ? [{ tab, summary }] : []));
}
